import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useDeepSelector } from './useDeepSelector';
import useProviderId from './useProviderId';
import type { IRootDispatch, RootState } from '../store/models';
import store from '../store';
import type { PublicProvider, PrivilegedProvider } from '../api/Providers';

function useActiveProvider(needsAdmin: true, forceReload: true): PrivilegedProvider;

function useActiveProvider(needsAdmin: true, forceReload: false): PrivilegedProvider;

function useActiveProvider(needsAdmin: true): PrivilegedProvider;

function useActiveProvider(): PublicProvider;

function useActiveProvider(needsAdmin: false): PublicProvider;

/**
 * useActiveProvider - hook to get cached
 * activeProvider from providers model
 * or if it isn't cached, load and
 * return it async.
 *
 * TODO: Separate this hook so that there's a version for clients, and a version for the logged in
 * provider.
 *
 * @param {boolean} needsAdmin When set to true, then the hook will set the provider as the active
 * provider in the providers store which will affect other components and hooks that use the
 * activeProviderId value
 *
 * @see SearchRank page to see it in use
 *
 * @returns {PublicProvider} provider
 */
function useActiveProvider(
  needsAdmin?: boolean,
  forceReload?: boolean,
): PublicProvider | PrivilegedProvider | null {
  const providerId = useProviderId();
  const provider = useDeepSelector<RootState, PublicProvider | PrivilegedProvider | null>(needsAdmin
    ? store.select.providers.activeProvider
    : store.select.providers.providerById(providerId));
  const dispatch = useDispatch<IRootDispatch>();

  useEffect(() => {
    if (!providerId || providerId < 0) return;
    if (!forceReload && provider?.id === providerId) return;

    dispatch.providers.loadProvider({
      providerId,
      setActive: needsAdmin,
      noLoading: true,
    });
  }, [
    dispatch.providers,
    needsAdmin,
    provider?.id,
    providerId,
  ]);

  if (!provider) return null;
  return needsAdmin
    ? provider as PrivilegedProvider
    : provider;
}

export default useActiveProvider;
